import {
    Card,
    CardBody,
    Button,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    CardHeader,
    CardFooter,
} from "@heroui/react";
import { LuPencil, LuTrash, LuPlus } from "react-icons/lu";
import { Link } from "react-router-dom";
import { useDataTable, useFetchData } from "@/hooks";
import { EmptyState, ErrorState, PageHeader } from "@/components/ui";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTablePagination,
    DataTableSkeleton,
    DeleteConfirmModal,
    FilterButton,
} from "@/components/data-table";
import { FilterData } from "./components";
import { useUser } from "@/context/UserContext";

// Initial filters
const initialFilters = {
    name: "",
    category_id: [],
};

const ProductSubCategoryData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("material-category.edit") || hasPermission("material-category.delete");
    const table = useDataTable(
        "material-sub-categories/data",
        "material-sub-categories/{id}/delete",
        {},
        initialFilters,
    );

    // Fetch filter options for label lookups
    const { data: categories } = useFetchData("select/material-category");

    // Filter options to pass to FilterData component (handle null with || [])
    const filterOptions = { categories: categories || [] };

    // Filter labels with dynamic getValueLabel
    const filterLabels = {
        name: { label: "Nama" },
        category_id: {
            label: "Kategori Induk",
            getValueLabel: (val) => {
                // Handle array values (multiple selection)
                if (Array.isArray(val)) {
                    return `${val.length} dipilih`;
                }
                const item = categories.find(
                    (c) => String(c.value) === String(val),
                );
                return item?.title || val;
            },
        },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description={table.error.message}
                onAction={table.refetch}
            />
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Nama Sub Kategori", skeletonWidth: "w-32" },
        { name: "Kategori Induk", skeletonWidth: "w-32" },
        { name: "Deskripsi", skeletonWidth: "w-48" },
        ...(hasAnyAction ? [{ name: "Aksi", align: "end", skeletonWidth: "w-16" }] : []),
    ];

    return (
        <div className="flex flex-col gap-4">
            {/* Page Header - Title/Breadcrumbs left, Actions right */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Sub Kategori Produk"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Produk", href: "/products" },
                        { label: "Sub Kategori" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("material-category.create") && (
                        <Button
                            as={Link}
                            to="/products-sub-category/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Sub Kategori
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col gap-4">
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada sub kategori yang cocok dengan filter."
                                    : "Belum ada data sub kategori produk."
                            }
                        />
                    ) : (
                        <Table
                            aria-label="Tabel Sub Kategori Produk"
                            shadow="none"
                        >
                            <TableHeader>
                                <TableColumn key="no">No.</TableColumn>
                                <TableColumn key="name">
                                    Nama Sub Kategori
                                </TableColumn>
                                <TableColumn key="category">
                                    Kategori Induk
                                </TableColumn>
                                <TableColumn key="description">
                                    Deskripsi
                                </TableColumn>
                                {hasAnyAction && (
                                    <TableColumn key="action" align="end">
                                        Aksi
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            {(table.pagination.page - 1) *
                                                table.pagination.perPage +
                                                table.items.indexOf(item) +
                                                1}
                                            .
                                        </TableCell>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>
                                            {item.category?.name || "-"}
                                        </TableCell>
                                        <TableCell>
                                            {item.description || "-"}
                                        </TableCell>
                                        {hasAnyAction && (
                                            <TableCell>
                                                <div className="relative flex items-center justify-end gap-2">
                                                    {hasPermission("material-category.edit") && (
                                                        <Tooltip content="Edit Sub Kategori">
                                                            <Button
                                                                isIconOnly
                                                                size="sm"
                                                                variant="light"
                                                                as={Link}
                                                                to={`/products-sub-category/${item.id}/edit`}
                                                            >
                                                                <LuPencil
                                                                    size={14}
                                                                    className="text-default-500"
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {hasPermission("material-category.delete") && (
                                                        <Tooltip content="Hapus Sub Kategori">
                                                            <Button
                                                                isIconOnly
                                                                size="sm"
                                                                color="danger"
                                                                variant="light"
                                                                onPress={() =>
                                                                    table.deleteState.openDeleteModal(
                                                                        item.id,
                                                                    )
                                                                }
                                                            >
                                                                <LuTrash size={14} />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />
        </div>
    );
};

export default ProductSubCategoryData;
